import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextProviderQuote } from './ProviderQuote';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderQuoteDA } from '../../../../services/admin/provider.quote.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ProviderQuote } from '../../../../models/provider.quote.model';
import { colorStatusProviderQuote } from '../../../../scripts/badge.color.script';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { getError } from '../../../../languages/translations/response';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { MediaDA } from '../../../../services/admin/media.service';

import AppPurchaseViewRequirements from '../component/PurchaseViewRequirements';
import AppPurchaseViewAmount from '../component/PurchaseViewAmount';
import AppPurchaseViewVouchers from '../component/PurchaseViewVouchers';
import AppPreloader from '../../../../components/Preloader';
import DocumentNavigation from '../../../../components/element/DocumentNavigation';

export interface ProviderQuoteViewPageProps {};

let errorResponse: Error, providerQuoteResponse: ProviderQuote;

const ProviderQuoteViewPage: React.FunctionComponent<ProviderQuoteViewPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextProviderQuote()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [providerQuote, setProviderQuote] = useState<ProviderQuote | null>(null)
  const [internalNotes, setInternalNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)

  const loadProviderQuote = async (id_company: string, id_provider_quote: string) => {
    await ProviderQuoteDA.getProviderQuote(id_company, id_provider_quote).then( async (response) => {
      if (response.status === 200) {
        providerQuoteResponse = response.data
        providerQuoteResponse.status = normalizeStatus(providerQuoteResponse.status)

        setProviderQuote(providerQuoteResponse)
        setInternalNotes(providerQuoteResponse.internal_notes ?? '')
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleAccept = async () => {
    if (!companyForUser || !providerQuote) return

    GlobalSwal.fire({
      title: lang.labels.acceptQuote,
      text: lang.labels.confirmAcceptQuote,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.acceptQuote,
      cancelButtonText: lang.labels.cancel,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ProviderQuoteDA.acceptProviderQuote(companyForUser.id, providerQuote.id).then( (response) => {
          if (response.status === 200 || response.status === 201) {
            const data = response.data

            GlobalSwal.fire({
              title: lang.labels.providerQuoteAccepted,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              if (data?.id_provider_order) {
                navigate('/main/purchase/order/view/' + data.id_provider_order)
              } else {
                loadProviderQuote(companyForUser.id, providerQuote.id)
              }
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const handleDecline = async () => {
    if (!companyForUser || !providerQuote) return

    GlobalSwal.fire({
      title: lang.labels.declineQuote,
      text: lang.labels.confirmDeclineQuote,
      icon: 'warning',
      input: 'textarea',
      inputLabel: lang.labels.declineReasonLabel,
      inputPlaceholder: lang.labels.declineReasonPlaceholder,
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return lang.labels.declineReasonRequired
        }
      },
      showCancelButton: true,
      confirmButtonText: lang.labels.declineQuote,
      cancelButtonText: lang.labels.cancel,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ProviderQuoteDA.declineProviderQuote(companyForUser.id, providerQuote.id, result.value).then( (response) => {
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.providerQuoteDeclined,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              loadProviderQuote(companyForUser.id, providerQuote.id)
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const handleSaveNotes = async () => {
    if (!companyForUser || !providerQuote) return
    setSavingNotes(true)

    await ProviderQuoteDA.updateInternalNotes(companyForUser.id, providerQuote.id, internalNotes).then( (response) => {
      if (response.status === 200) {
        GlobalSwal.fire({
          title: lang.labels.successful,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })

    setSavingNotes(false)
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadProviderQuote(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.providerQuotes, branch: lang.labels.viewProviderQuote}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && providerQuote
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            {/* Header */}
            <div className="card card-flush mb-5">
              <div className="card-header pt-3">
                <div className="card-title align-items-start flex-column">
                  <div className="d-flex align-items-center gap-3">
                    <h1 className="mb-0">{providerQuote.provider_quote_number}</h1>
                    {companyForUser && param.id && <DocumentNavigation navKey="nav_provider_quotes" currentId={param.id} companyId={companyForUser.id} basePath="/main/purchase/quote/view/" />}
                  </div>
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.status}:
                    <span className={`badge badge-light-${colorStatusProviderQuote(providerQuote.status)} text-uppercase fs-7 ms-2`}>{statusDocumentValue(lang, providerQuote.status)}</span>
                  </label>
                </div>
                <div className="card-toolbar">
                  <div className="d-flex justify-content-end">
                    { providerQuote.status === 'received' &&
                      <>
                        <button className="btn btn-success mb-1 me-2" type="button" onClick={handleAccept}>
                          <div className="d-flex align-items-center">
                            <span className="svg-icon svg-icon-2 ms-0">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M10.3 14.3L11 13.6L7.70002 10.3C7.30002 9.9 6.7 9.9 6.3 10.3C5.9 10.7 5.9 11.3 6.3 11.7L10.3 15.7C9.9 15.3 9.9 14.7 10.3 14.3Z" fill="black"/>
                                <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM11.7 15.7L17.7 9.70001C18.1 9.30001 18.1 8.69999 17.7 8.29999C17.3 7.89999 16.7 7.89999 16.3 8.29999L11 13.6L7.70001 10.3C7.30001 9.89999 6.69999 9.89999 6.29999 10.3C5.89999 10.7 5.89999 11.3 6.29999 11.7L10.3 15.7C10.5 15.9 10.8 16 11 16C11.2 16 11.5 15.9 11.7 15.7Z" fill="black"/>
                              </svg>
                            </span>
                            {lang.labels.acceptQuote}
                          </div>
                        </button>
                        <button className="btn btn-danger mb-1" type="button" onClick={handleDecline}>
                          <div className="d-flex align-items-center">
                            <span className="svg-icon svg-icon-2 ms-0">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M12 10.6L14.8 7.8C15.2 7.4 15.8 7.4 16.2 7.8C16.6 8.2 16.6 8.80002 16.2 9.20002L13.4 12L12 10.6ZM10.6 12L7.8 14.8C7.4 15.2 7.4 15.8 7.8 16.2C8 16.4 8.3 16.5 8.5 16.5C8.7 16.5 8.99999 16.4 9.19999 16.2L12 13.4L10.6 12Z" fill="black"/>
                                <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM13.4 12L16.2 9.20001C16.6 8.80001 16.6 8.19999 16.2 7.79999C15.8 7.39999 15.2 7.39999 14.8 7.79999L12 10.6L9.2 7.79999C8.8 7.39999 8.2 7.39999 7.8 7.79999C7.4 8.19999 7.4 8.80001 7.8 9.20001L10.6 12L7.8 14.8C7.4 15.2 7.4 15.8 7.8 16.2C8 16.4 8.3 16.5 8.5 16.5C8.7 16.5 9 16.4 9.2 16.2L12 13.4L14.8 16.2C15 16.4 15.3 16.5 15.5 16.5C15.7 16.5 16 16.4 16.2 16.2C16.6 15.8 16.6 15.2 16.2 14.8L13.4 12Z" fill="black"/>
                              </svg>
                            </span>
                            {lang.labels.declineQuote}
                          </div>
                        </button>
                      </>
                    }
                    { providerQuote.status === 'accepted' && providerQuote.id_provider_order &&
                      <Link to={"/main/purchase/order/view/" + providerQuote.id_provider_order} className="btn btn-primary mb-1">
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black"/>
                              <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.seeProviderOrder}
                        </div>
                      </Link>
                    }
                  </div>
                </div>
              </div>
              {/* Detail section */}
              <div className="card-body pt-0">
                <div className="row mb-5">
                  <div className="col-lg-6">
                    <table className="table table-flush fs-6 fw-bold gy-1">
                      <tbody>
                        <tr>
                          <td className="text-muted min-w-150px">{lang.labels.sourceQuoteNumber}</td>
                          <td className="text-gray-800">{providerQuote.source_quote_number || '-'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">{lang.labels.sourceCompany}</td>
                          <td className="text-gray-800">{providerQuote.source_company?.name || '-'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">{lang.labels.provider}</td>
                          <td className="text-gray-800">{providerQuote.provider?.name || [providerQuote.provider?.first_name, providerQuote.provider?.last_name].filter(Boolean).join(' ') || '-'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">{lang.labels.concerning}</td>
                          <td className="text-gray-800">{providerQuote.concerning || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-lg-6">
                    <table className="table table-flush fs-6 fw-bold gy-1">
                      <tbody>
                        <tr>
                          <td className="text-muted min-w-150px">{lang.labels.createdDate}</td>
                          <td className="text-gray-800">{dateFormat(lang.code, providerQuote.created_date)}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">{lang.labels.validity}</td>
                          <td className="text-gray-800">{providerQuote.valid_until ? dateFormat(lang.code, providerQuote.valid_until) : '-'}</td>
                        </tr>
                        { providerQuote.status === 'accepted' && providerQuote.accepted_at &&
                          <tr>
                            <td className="text-muted">{lang.labels.accepted}</td>
                            <td className="text-gray-800">{dateFormat(lang.code, providerQuote.accepted_at)}</td>
                          </tr>
                        }
                        { providerQuote.status === 'declined' && providerQuote.declined_at &&
                          <tr>
                            <td className="text-muted">{lang.labels.declined}</td>
                            <td className="text-gray-800">{dateFormat(lang.code, providerQuote.declined_at)}</td>
                          </tr>
                        }
                        <tr>
                          <td className="text-muted">{lang.labels.currency}</td>
                          <td className="text-gray-800">{providerQuote.currency?.code ?? '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Requirements */}
            <AppPurchaseViewRequirements record={providerQuote}></AppPurchaseViewRequirements>

            {/* Amounts */}
            <AppPurchaseViewAmount purchase={"quote"} record={providerQuote}></AppPurchaseViewAmount>

            {/* Internal Notes */}
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">{lang.labels.internalNotes}</div>
              </div>
              <div className="card-body pt-0">
                <textarea
                  className="form-control mb-3"
                  rows={4}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder={lang.labels.internalNotes}
                />
                <button
                  className="btn btn-sm btn-primary"
                  type="button"
                  disabled={savingNotes}
                  onClick={handleSaveNotes}
                >
                  {savingNotes && <span className="spinner-border spinner-border-sm me-2" />}
                  {lang.labels.saveNotes}
                </button>
              </div>
            </div>

            {/* Vouchers */}
            <AppPurchaseViewVouchers purchase={"quote"} record={providerQuote} entity_type="provider-quote" id_entity={providerQuote?.id} id_company={companyForUser?.id} showVoucherActions={true} showDeleteAction={true} onVouchersChanged={() => { if (companyForUser && param.id) loadProviderQuote(companyForUser.id, param.id) }}></AppPurchaseViewVouchers>
          </div>

          {/* Right sidebar - source company info */}
          <div className="flex-column flex-lg-row-auto order-1 order-lg-2 w-lg-250px w-xl-300px mb-5 mb-lg-0">
            <div className="card card-flush">
              <div className="card-header">
                <div className="card-title">{lang.labels.sourceCompany}</div>
              </div>
              <div className="card-body pt-0">
                { providerQuote.source_company?.logo &&
                  <div className="mb-4">
                    <img src={providerQuote.source_company.logo} alt={providerQuote.source_company.name} className="mw-100" style={{maxHeight: '60px'}} />
                  </div>
                }
                <div className="fw-bold fs-6 mb-2">{providerQuote.source_company?.name}</div>
                { providerQuote.source_company?.address &&
                  <div className="fs-7 text-muted">
                    {[providerQuote.source_company.address.street, providerQuote.source_company.address.number].filter(Boolean).join(' ')}<br/>
                    {[providerQuote.source_company.address.zip_code, providerQuote.source_company.address.city].filter(Boolean).join(' ')}<br/>
                    {providerQuote.source_company.address.country}
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ProviderQuoteViewPage;
